/**
 * How often a VAT period is filed, from the period itself.
 *
 * Lives here rather than in the header component for two reasons. It is
 * rule-table knowledge — which deadline rule implies which cadence — and not
 * presentation; and the UI tree is scanned by `NoRuleIdsInUi` for exactly these
 * identifiers, because they must never reach a user. Comparing against them is
 * fine, rendering them is not, and keeping the comparison out of the component
 * keeps that line clean.
 *
 * `vat_reporting_periods` has no cadence column — the header used to read
 * `period_type`, which does not exist on that table, so the ternary always fell
 * through to "Kvartalsvis" and a helår period was labelled quarterly (S3).
 */
const MONTHLY_RULES = new Set(['R-DL-1', 'R-DL-2']);
const QUARTERLY_RULES = new Set(['R-DL-3']);

/**
 * @returns {'Månadsvis'|'Kvartalsvis'|'Årsvis'|null} null when the period says
 *   nothing about its own cadence, so the caller can omit the chip rather than
 *   guess.
 */
export function cadenceLabelForPeriod(period) {
    const rule = period && period.due_date_rule_id;
    if (rule) {
        if (MONTHLY_RULES.has(rule)) return 'Månadsvis';
        if (QUARTERLY_RULES.has(rule)) return 'Kvartalsvis';
        // Every remaining deadline rule is a helår track.
        return 'Årsvis';
    }

    // Fallback for rows written before the rule id was stored: the period's own
    // span is what it is.
    if (!period || !period.start_date || !period.end_date) return null;
    const start = new Date(`${String(period.start_date).slice(0, 10)}T00:00:00`);
    const end = new Date(`${String(period.end_date).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const days = Math.round((end - start) / 86400000) + 1;
    if (days <= 45) return 'Månadsvis';
    if (days <= 135) return 'Kvartalsvis';
    return 'Årsvis';
}

export default cadenceLabelForPeriod;
